import React from "react";
import {
  getLinkHref,
  getThemeColor,
  getThemeColorButtonVariant
} from "../../utils";
import { formatMultilinesText } from "../../../../helpers/markdown";
import {
  Box,
  Typography,
  Hidden,
  Button,
  styled,
  makeStyles,
  useTheme
} from "@material-ui/core";
import LocalizedLink from "../../../../components/localized-link";
import { useInView } from "react-intersection-observer";

const highTechBackgroundSize = "300%";

const useGlobalStyles = makeStyles({
  "@global": {
    "@keyframes highTechTextAnimation": {
      to: {
        backgroundPosition: `${highTechBackgroundSize} 0`
      }
    }
  }
});

export const HighTechTypography = styled(({ children, ...props }) => (
  <Typography {...props} component="span">
    {children}
  </Typography>
))(({ theme }) => ({
  fontFamily: "Reckless, Georgia",
  fontSize: `clamp(64px, 10vw, 190px)`,
  lineHeight: 1.3,
  color: theme.palette.common.white,
  fontWeight: 400,
  letterSpacing: "-0.04em",
  [theme.breakpoints.down("sm")]: {
    fontSize: `12vw`
  },
  [theme.breakpoints.down("xs")]: {
    fontSize: `82px`
  }
}));

export const HomepageHighColoredTypography = styled(HighTechTypography)(
  ({ theme }) => ({
    background: `linear-gradient(70deg, #5552FF, #FF00FF, #FF0000, #950FFF, #3086C5, #5552FF) 0 0 / ${highTechBackgroundSize} 100%`,
    color: "transparent",
    backgroundClip: "text",
    "-webkit-background-clip": "text",
    animation: "highTechTextAnimation 8s infinite linear",
    [theme.breakpoints.down("xs")]: {
      marginRight: `16px`
    }
  })
);

export const PartnersHighColoredTypography = styled(HighTechTypography)(
  ({ theme }) => ({
    background: `linear-gradient(70deg, rgb(30, 30, 30), #858585, rgb(30, 30, 30)) 0 0 / ${highTechBackgroundSize} 100%`,
    color: "transparent",
    backgroundClip: "text",
    "-webkit-background-clip": "text",
    animation: "highTechTextAnimation 10s infinite linear",
    [theme.breakpoints.down("xs")]: {
      marginRight: `16px`
    }
  })
);

export const SubtitleTypography = styled(({ children, ...props }) => (
  <Typography {...props} component="h2">
    {children}
  </Typography>
))(({ theme }) => ({
  fontFamily: "Reckless, Georgia",
  fontSize: "3rem",
  lineHeight: 1.1,
  textAlign: "center",
  color: theme.palette.common.white,
  fontWeight: 400,
  [theme.breakpoints.down("sm")]: {
    fontSize: `2.2rem`
  }
}));

export default function CardVariantHighTech2023(
  props: Queries.CardFragmentFragment
) {
  const ctaLinkHref = getLinkHref(props.ctaLink);
  const theme = useTheme();
  useGlobalStyles();

  const [ref, ctaInView] = useInView({ initialInView: true });

  return (
    <>
      {/* HIGH TECH custom title */}
      <Box
        display={{ xs: "none", sm: "flex" }}
        flexDirection="row"
        alignItems="center"
        justifyContent={{ xs: "center", sm: "space-between" }}
        position="absolute"
        top="45%"
        left="50%"
        style={{ transform: "translate(-50%, -50%)" }}
        width="100%"
        maxWidth={{
          xs: "85%",
          md: "75%"
        }}
        paddingX={3}
        component="h1"
      >
        {props.variant === "homepage-hightech-2023" && (
          <HomepageHighColoredTypography>high</HomepageHighColoredTypography>
        )}
        {props.variant === "partners-hightech-2023" && (
          <PartnersHighColoredTypography>high</PartnersHighColoredTypography>
        )}
        <HighTechTypography>tech</HighTechTypography>
      </Box>

      {/* Subtitle + CTA */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        position="absolute"
        bottom={{
          xs: theme.spacing(3),
          sm: theme.spacing(3),
          md: theme.spacing(6),
          lg: theme.spacing(7)
        }}
        left="50%"
        style={{ transform: "translate(-50%, 0%)" }}
        width="100%"
        maxWidth={{ xs: "500px" }}
        paddingX={2}
      >
        <Box
          display={{ xs: "flex", sm: "none" }}
          alignItems="center"
          justifyContent="center"
          marginBottom={0.5}
          component="h1"
        >
          {props.variant === "homepage-hightech-2023" && (
            <HomepageHighColoredTypography>high</HomepageHighColoredTypography>
          )}
          {props.variant === "partners-hightech-2023" && (
            <PartnersHighColoredTypography>high</PartnersHighColoredTypography>
          )}
          <HighTechTypography>tech</HighTechTypography>
        </Box>

        {props.subtitle && props.subtitle && (
          <Box marginBottom={{ xs: 3, sm: 0 }}>
            <SubtitleTypography
              style={{
                color: getThemeColor(props.subtitleTheme, "white")
              }}
              dangerouslySetInnerHTML={{
                __html: formatMultilinesText(props.subtitle)
              }}
            />
          </Box>
        )}

        {props.cta && ctaLinkHref && (
          <Hidden smUp implementation="css">
            <Button
              variant="contained"
              component={LocalizedLink}
              to={ctaLinkHref}
              title={props.cta}
              color={getThemeColorButtonVariant(props.ctaTheme, "yellow")}
              fullWidth
              ref={props.variant === "partners-hightech-2023" ? ref : undefined}
            >
              {props.cta}
            </Button>
          </Hidden>
        )}

        {props.variant === "partners-hightech-2023" && (
          <Hidden smUp implementation="css">
            <Box mt={2}>
              <Button
                variant="contained"
                component={LocalizedLink}
                to={"/"}
                fullWidth
                color="secondary"
              >
                Visit consumer site
              </Button>
            </Box>
          </Hidden>
        )}
      </Box>

      {props.variant === "partners-hightech-2023" && (
        <Hidden smUp implementation="css">
          <Box
            position="fixed"
            bottom="0"
            left={0}
            width="100%"
            minHeight={{ xs: "auto", md: "82px" }}
            display="flex"
            style={{
              opacity: !ctaInView ? 1 : 0,
              pointerEvents: !ctaInView ? "all" : "none",
              backgroundColor: "rgba(30, 30, 30, 0.92)",
              backdropFilter: "blur(6.5px)",
              transition: "opacity 250ms ease-in-out"
            }}
            justifyContent={{ xs: "center", md: "space-between" }}
            alignItems="center"
            padding={{ xs: 2, md: 2 }}
            zIndex={9000}
          >
            <Button
              variant="contained"
              component={LocalizedLink}
              target="_blank"
              to={ctaLinkHref}
              fullWidth
              color="primary"
            >
              {props.cta}
            </Button>
          </Box>
        </Hidden>
      )}
    </>
  );
}
